/**
 * Centralized query key factory.
 *
 * Rule of thumb: one key per endpoint family, with parameters appended so
 * TanStack Query can invalidate surgically. Exporting as a single object
 * makes targeted invalidation readable: `qc.invalidateQueries({ queryKey: keys.ShoppingLists.all })`.
 */

export const keys = {
  auth: {
    me: ['auth', 'me'] as const,
    // T27.2.10.1 — linked sign-in methods for the Settings link/unlink UI.
    identities: ['auth', 'identities'] as const,
  },
  households: {
    current: ['households', 'current'] as const,
    list: ['households', 'list'] as const,
    users: ['households', 'users'] as const,
    invites: ['households', 'invites'] as const,
    selectedStores: ['households', 'selected-stores'] as const,
    // T12.2 — store-preference entries (per-store category affinity) +
    // household-wide brand like/avoid.
    storePreferences: ['households', 'store-preferences'] as const,
    brandPreferences: ['households', 'brand-preferences'] as const,
    // T12.2 (step 8) — household-wide system-product like/avoid.
    systemProductPreferences: ['households', 'systemproduct-preferences'] as const,
    // T12.5.1 (C) — household catalog preference signals (read-only): per-product
    // brand + product preferences (preferred/substitute/exclude, with value),
    // rolled up to the household; render the Deals/Planning stacked badge set.
    catalogPreferenceSignals: ['households', 'catalog-preference-signals'] as const,
  },
  ShoppingLists: {
    all: ['shopping-lists'] as const,
    active: ['shopping-lists', 'active'] as const,
    one: (listId: string) => ['shopping-lists', listId] as const,
  },
  HouseholdProducts: {
    all: ['household-products'] as const,
    search: (query: string) => ['household-products', 'search', query] as const,
    one: (productId: string) => ['household-products', productId] as const,
    offers: (productId: string) => ['household-products', productId, 'offers'] as const,
    preferences: (productId: string) =>
      ['household-products', productId, 'preferences'] as const,
    // T36.3 — the measurement settings picker's valid choices for a product.
    measurementChoices: (productId: string) =>
      ['household-products', productId, 'measurement-choices'] as const,
  },
  brands: {
    all: ['brands'] as const,
  },
  // T12.5 — the "Suggested · likely needed soon" shortlist for the Add-to-list
  // sheet. Keyed on the excluded ids (the active list's items) so flipping which
  // items are on the list refetches the shortlist with the new exclusions.
  suggestions: {
    list: (excludeIds: string[]) =>
      ['suggestions', 'products', { excludeIds }] as const,
  },
  SystemProducts: {
    search: (params: unknown) => ['system-products', 'search', params] as const,
    offers: (systemProductId: string) =>
      ['system-products', systemProductId, 'offers'] as const,
  },
  RetailerOffers: {
    byStore: (storeId: string) => ['retailer-offers', 'store', storeId] as const,
    one: (storeId: string, offerId: string) =>
      ['retailer-offers', 'store', storeId, offerId] as const,
    search: (query: string) => ['retailer-offers', 'search', query] as const,
  },
  FlyerDeals: {
    all: (includeExpired: boolean) => ['flyer-deals', { includeExpired }] as const,
    // T55.3 — the fit-ranked search read. Keyed on the full bar text (q) +
    // optional store scope, so a different query re-fetches rather than
    // serving a stale cached result.
    search: (q: string, storeIds?: string[], rewrite?: 'off') =>
      [
        'flyer-deals',
        'search',
        { q, storeIds: storeIds?.slice().sort() ?? null, rewrite: rewrite ?? null },
      ] as const,
    suggestions: (q: string) => ['flyer-deals', 'search-suggestions', q] as const,
  },
  stores: {
    all: ['stores'] as const,
  },
};
